/** Labels for Admin AML screening (not workspace vendor AML). */

/** AiPrise backend poll: ~3s × 20 attempts before the screen endpoint returns. */
export const AIPRISE_POLL_TIMEOUT_MS = 90_000
export const VOVE_SCREEN_TIMEOUT_MS = 60_000

export const SCREENING_PROVIDER_OPTIONS = [
  { label: 'Vove', value: 'vove' },
  { label: 'AiPrise', value: 'aiprise' },
]

export const SCREENING_MODE_OPTIONS = [
  { label: 'One time', value: 'one_time' },
  { label: 'Ongoing', value: 'ongoing' },
]

export const SUBJECT_TYPE_OPTIONS = [
  { label: 'Entity', value: 'entity' },
  { label: 'Individual', value: 'individual' },
]

export const SCREENING_PROVIDER_FILTER_OPTIONS = [
  { label: 'All providers', value: 'all' },
  ...SCREENING_PROVIDER_OPTIONS,
]

export const SUBJECT_TYPE_FILTER_OPTIONS = [
  { label: 'All types', value: 'all' },
  ...SUBJECT_TYPE_OPTIONS,
]

export const SCREENING_MODE_FILTER_OPTIONS = [
  { label: 'All modes', value: 'all' },
  ...SCREENING_MODE_OPTIONS,
]

export function screeningProviderLabel(provider) {
  if (provider === 'aiprise') return 'AiPrise'
  if (provider === 'vove') return 'Vove'
  return provider || '—'
}

export function screeningModeLabel(mode) {
  if (mode === 'ongoing') return 'Ongoing'
  if (mode === 'one_time') return 'One time'
  return mode || '—'
}

export function subjectTypeLabel(type) {
  if (type === 'entity') return 'Entity'
  if (type === 'individual') return 'Individual'
  return type || '—'
}

export function screeningHitsLabel(totalHits) {
  const n = Number(totalHits) || 0
  if (n === 0) return 'No matches'
  return `${n} hit${n === 1 ? '' : 's'}`
}

export function screeningRequestTimeoutMs(provider) {
  return provider === 'aiprise' ? AIPRISE_POLL_TIMEOUT_MS : VOVE_SCREEN_TIMEOUT_MS
}

/**
 * Loader copy while the backend runs the screen.
 * AiPrise may stay PENDING and poll for up to ~60s before returning hits.
 */
export function screeningLoaderMessages(provider) {
  if (provider === 'aiprise') {
    return [
      'Starting AiPrise verification…',
      'AiPrise is PENDING — waiting for COMPLETED…',
      'Still polling AiPrise for AML results…',
      'Collecting matches — this can take up to a minute…',
    ]
  }
  return ['Running AML Screening…']
}

/** Drop empty / "all" filter values before sending list query params. */
export function buildScreeningListParams(params = {}) {
  const out = {}
  Object.entries(params).forEach(([key, value]) => {
    if (value == null || value === '' || value === 'all') return
    out[key] = value
  })
  return out
}

const asArray = (value) => (Array.isArray(value) ? value : [])

const firstString = (...candidates) => {
  for (const value of candidates) {
    if (typeof value === 'string' && value.trim()) return value.trim()
    if (typeof value === 'number' && Number.isFinite(value)) return String(value)
  }
  return ''
}

const isVoveHit = (item) =>
  item &&
  typeof item === 'object' &&
  (typeof item.fullName === 'string' ||
    typeof item.sourceList === 'string' ||
    typeof item.sourceType === 'string')

const isAipriseResultEnvelope = (value) =>
  value &&
  typeof value === 'object' &&
  !Array.isArray(value) &&
  (value.aml_info != null || value.verification_session_id != null || value.aiprise_summary != null)

/**
 * Raw AiPrise payload may live on `provider_response`, legacy `vove_response`,
 * or (incorrectly) inside `found_records` itself.
 */
export function getProviderResponse(screening) {
  if (!screening) return null
  const direct = screening.provider_response ?? screening.vove_response ?? null
  if (direct && typeof direct === 'object') return direct

  const records = screening.found_records
  if (isAipriseResultEnvelope(records)) return records
  if (Array.isArray(records) && records.length === 1 && isAipriseResultEnvelope(records[0])) {
    return records[0]
  }
  return null
}

export function getAipriseAmlInfo(screening) {
  const response = getProviderResponse(screening)
  if (response?.aml_info && typeof response.aml_info === 'object') {
    return response.aml_info
  }
  return null
}

/** Summary chips for AiPrise (APPROVED / hit counts by category). */
export function getAipriseHitSummary(screening) {
  const aml = getAipriseAmlInfo(screening)
  const response = getProviderResponse(screening)
  if (!aml && !response) return null

  const summary = aml?.aml_hit_summary && typeof aml.aml_hit_summary === 'object'
    ? aml.aml_hit_summary
    : {}

  return {
    result: firstString(aml?.result, response?.aiprise_summary?.verification_result) || null,
    status: firstString(aml?.status, response?.status) || null,
    numHits: Number(aml?.num_hits ?? screening?.total_hits ?? 0) || 0,
    categories: Object.entries(summary).map(([key, value]) => ({
      key,
      label: key.replaceAll('_', ' '),
      count: Number(value) || 0,
    })),
  }
}

function normalizeVoveHit(item) {
  return {
    ...item,
    fullName: firstString(item.fullName, item.name) || '—',
    aliasNames: asArray(item.aliasNames ?? item.aliases),
    dob: firstString(item.dob, item.dateOfBirth) || 'N/A',
    placeOfBirth: firstString(item.placeOfBirth) || 'N/A',
    sourceType: firstString(item.sourceType, item.entityType) || '',
    sourceList: firstString(item.sourceList, item.sourceId) || '',
    citizenship: asArray(item.citizenship),
    address: asArray(item.address),
    sanctionDetails: asArray(item.sanctionDetails),
    links: asArray(item.links),
    otherInformation: asArray(item.otherInformation),
    _provider: 'vove',
  }
}

/** "ADVERSE_MEDIA" → "Adverse media" for hit/source type chips. */
export function amlHitTypeLabel(type) {
  if (!type) return ''
  const text = String(type).replaceAll('_', ' ').replaceAll('-', ' ').trim().toLowerCase()
  return text.charAt(0).toUpperCase() + text.slice(1)
}

/**
 * Badge colors per hit type. Sanctions are the most severe, then PEP /
 * warnings, then adverse media; anything else stays neutral.
 */
export function amlHitTypeBadgeClass(type) {
  const t = String(type || '').toLowerCase()
  if (t.includes('sanction')) return 'border-red-200 bg-red-50 text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300'
  if (t.includes('pep') || t.includes('warning')) return 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-300'
  if (t.includes('media')) return 'border-orange-200 bg-orange-50 text-orange-700 dark:border-orange-900 dark:bg-orange-950/40 dark:text-orange-300'
  return 'border-border bg-muted text-muted-foreground'
}

/**
 * AiPrise media dates are unix seconds as strings ("1492128000").
 * Falls back to the raw value for ISO strings; '' when missing/invalid.
 */
export function formatAmlMediaDate(value) {
  if (value == null || value === '') return ''
  const n = Number(value)
  const date = Number.isFinite(n)
    ? new Date(n < 1e12 ? n * 1000 : n)
    : new Date(String(value))
  if (Number.isNaN(date.getTime())) return ''
  // Same MM/DD/YYYY as filters.formatDate — not imported to avoid a stores ↔ lib cycle.
  return date.toLocaleString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
}

const uniqueStrings = (list) => [...new Set(list.filter(Boolean))]

function normalizeAipriseHit(item) {
  // AiPrise entity_hits keep the real match data in `aml_hits[]`
  // (hit_type, source_details, media, fields) — flatten it for the table/dialog.
  const amlHits = asArray(item.aml_hits).filter((hit) => hit && typeof hit === 'object')

  const types = asArray(item.types ?? item.match_types ?? item.aml_types)
  const aliases = uniqueStrings(
    asArray(item.aka ?? item.aliases ?? item.alias_names ?? item.also_known_as)
      .map((alias) => (typeof alias === 'string' ? alias.trim() : alias?.name || alias?.alias || '')),
  )

  const countries = uniqueStrings([
    ...asArray(item.countries ?? item.country_codes ?? item.citizenship ?? item.nationalities)
      .map((c) => (typeof c === 'string' ? c : c?.code || c?.name || '')),
    ...amlHits.flatMap((hit) => asArray(hit.source_details?.country_codes)),
  ])

  const hitTypes = uniqueStrings(amlHits.map((hit) => firstString(hit.hit_type)))
  const sourceNames = uniqueStrings(amlHits.map((hit) => firstString(hit.source_details?.name)))
  const amlTypes = uniqueStrings(amlHits.flatMap((hit) => asArray(hit.source_details?.aml_types)))

  const media = amlHits
    .flatMap((hit) => asArray(hit.media))
    .filter((m) => m && typeof m === 'object' && (m.title || m.url || m.snippet))
    .map((m) => ({
      title: firstString(m.title, m.url) || 'Untitled article',
      url: firstString(m.url),
      snippet: firstString(m.snippet),
      date: formatAmlMediaDate(m.date),
    }))

  // `fields` are { name/tag, value } pairs on sanction/PEP hits.
  const fieldDetails = amlHits
    .flatMap((hit) => asArray(hit.fields))
    .map((f) => {
      if (typeof f === 'string') return f
      const label = firstString(f?.name, f?.tag, f?.label)
      const value = firstString(f?.value)
      if (!value) return ''
      return label ? `${label}: ${value}` : value
    })
    .filter(Boolean)

  const sourceType = firstString(
    item.sourceType,
    hitTypes.join(', '),
    types[0],
    Array.isArray(item.datasets) ? item.datasets[0]?.type : '',
  )

  return {
    ...item,
    fullName: firstString(item.fullName, item.name, item.entity_name, item.matched_name) || '—',
    aliasNames: aliases,
    dob: firstString(item.dob, item.date_of_birth, item.birth_date, item.birth_year) || 'N/A',
    placeOfBirth: firstString(item.placeOfBirth, item.place_of_birth) || 'N/A',
    sourceType: sourceType ? amlHitTypeLabel(sourceType) : '',
    sourceList: firstString(
      item.sourceList,
      sourceNames.join(', '),
      item.list_name,
      item.dataset,
      item.data_source,
      item.source,
      Array.isArray(item.datasets) ? item.datasets[0]?.name : '',
    ) || 'AiPrise',
    citizenship: countries,
    address: asArray(item.address ?? item.addresses).map((a) =>
      typeof a === 'string' ? a : [a?.address_line, a?.city, a?.country_code].filter(Boolean).join(', '),
    ).filter(Boolean),
    sanctionDetails: [
      ...asArray(item.sanctionDetails ?? item.details ?? item.reasons),
      ...fieldDetails,
    ],
    links: asArray(item.links ?? item.urls).map((l) => (typeof l === 'string' ? l : l?.url || '')).filter(Boolean),
    otherInformation: asArray(item.otherInformation ?? item.notes),
    entityType: firstString(item.entityType, item.entity_type) || '',
    amlTypes: amlTypes.map(amlHitTypeLabel),
    media,
    resolutionStatus: firstString(item.resolution_status),
    _provider: 'aiprise',
  }
}

function extractRawHitList(screening) {
  const records = screening?.found_records
  const aml = getAipriseAmlInfo(screening)

  if (Array.isArray(records)) {
    // Backend already stored a hit array (Vove shape or AiPrise entity_hits).
    if (records.length && !isAipriseResultEnvelope(records[0])) return records
    // found_records = [full AiPrise envelope]
    if (records.length === 1 && isAipriseResultEnvelope(records[0])) {
      return asArray(records[0]?.aml_info?.entity_hits)
    }
  }

  if (isAipriseResultEnvelope(records)) {
    return asArray(records.aml_info?.entity_hits)
  }

  if (aml) return asArray(aml.entity_hits)
  return []
}

/**
 * Normalize `found_records` for the shared hits table.
 * Vove hits use fullName/sourceList; AiPrise may store entity_hits or the full result envelope.
 */
export function normalizeFoundRecords(screening) {
  const provider = screening?.provider
  const raw = extractRawHitList(screening)

  return raw
    .filter((item) => item && typeof item === 'object')
    .map((item) => {
      if (provider === 'aiprise' || (!isVoveHit(item) && (item.name || item.entity_name || item.aka))) {
        return normalizeAipriseHit(item)
      }
      if (isVoveHit(item) || provider === 'vove') {
        return normalizeVoveHit(item)
      }
      // Unknown shape — still try AiPrise then Vove fields.
      return isVoveHit(item) ? normalizeVoveHit(item) : normalizeAipriseHit(item)
    })
}

export function screeningHasHits(screening) {
  const total = Number(screening?.total_hits)
  if (Number.isFinite(total) && total > 0) return true
  return normalizeFoundRecords(screening).length > 0
}
